import { useDemo } from '../../api/app-context';
import { useState } from 'react';
import type { SubjectRecord } from '@ni/shared';
import { useWrongPerson } from '../../api/hooks';
import { completedResearchRun, isRestartOnlyRun } from '../../lib/run-state';
import { IdentityLine } from '../brief/identity-line';
import { ConfirmModal } from '../ui/confirm-modal';
import { IDENTITY_CONFIRMED } from '../../lib/copy';

export function PersonIdentity({
  record,
  subjectId,
}: {
  record: SubjectRecord;
  subjectId: string;
}) {
  const demo = useDemo();
  const [rejecting, setRejecting] = useState(false);
  const wrongPerson = useWrongPerson(subjectId);
  const identity = record.identity;
  const completedResearch = completedResearchRun(record.runs);
  const latest = [...record.runs].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  const liveRunId =
    latest && latest.state !== 'complete' && !isRestartOnlyRun(latest) ? latest.id : null;
  if (!identity) return null;
  return (
    <>
      <details className="person-identity">
        <summary>{IDENTITY_CONFIRMED}</summary>
        <IdentityLine
          identity={identity}
          /* Confirmed identifiers the standing version does not name — an
             import that attached to this person after their identity was
             settled (owner's ruling, 2026-09-15). Almost always zero. */
          moreOnRecord={record.anchors_on_record.length}
          onWrongPerson={demo || completedResearch ? undefined : () => setRejecting(true)}
          pending={wrongPerson.isPending}
          error={wrongPerson.isError ? wrongPerson.error : null}
        />
      </details>

      {/* The gate must also cover a modal already open when the dossier completes mid-confirmation. */}
      {!demo && rejecting && !completedResearch ? (
        <ConfirmModal
          title="Wrong person"
          line="This will remove what was found for this identity. It will not come back for this person."
          confirmLabel="Remove it"
          workingLabel="Removing…"
          working={wrongPerson.isPending}
          onConfirm={() => wrongPerson.mutate(liveRunId, { onSuccess: () => setRejecting(false) })}
          dismissLabel="Cancel"
          onClose={() => setRejecting(false)}
        />
      ) : null}
    </>
  );
}
